import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { Icon } from 'views/components/molecules/Icon';
import { InputText } from 'views/components/atoms/InputText';
import { Color, MAX_WIDTH } from 'views/constants';
import { Button } from 'views/components/atoms/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { localeIds } from 'locals';
import { ErrorMessage } from 'views/components/molecules/ErrorMessage';
import { Typography } from 'views/components/atoms/Typography';
import { Spacer } from 'views/components/atoms/Spacer';
import { validateEmailAddress } from 'utils/string';

const Wrapper = styled.div`
  margin: 0;
  padding: 0 16px;
  background-color: ${Color.C6};
  border-bottom: 1px solid ${Color.C14};
`;

const InputWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
  padding: 16px 0;

  &:not(:first-of-type) {
    border-top: 1px solid ${Color.C14};
  }
`;

interface InputAreaProps {
  defaultEmail: string;
  setEmail(value: string): void;
}

const InputArea: React.FC<InputAreaProps> = ({ defaultEmail, setEmail }) => (
  <Wrapper>
    <InputWrapper>
      <FormattedMessage id={localeIds.signUpEmailForm.email}>
        {(text: string): React.ReactElement => (
          <InputText
            label={<Icon type="mail" />}
            onChange={setEmail}
            defaultValue={defaultEmail}
            placeholder={text}
            type="email"
            required
          />
        )}
      </FormattedMessage>
    </InputWrapper>
  </Wrapper>
);

const TextWrapper = styled.div`
  width: ${MAX_WIDTH};
  margin: auto;
  text-align: center;
`;

const MessageWrapper = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  width: ${MAX_WIDTH};
  margin: auto;
  padding: 16px 0 28px;
`;

interface SignUpEmailFormProps {
  defaultEmail?: string;
  errorMessage?: string;
  onPost(email: string): void;
}

type EmailValidator = (email: string) => string | undefined;

export const SignUpEmailForm: React.FC<SignUpEmailFormProps> = ({
  defaultEmail,
  errorMessage,
  onPost,
}) => {
  const intl = useIntl();
  const [email, setEmail] = useState(defaultEmail || '');
  const [localErrorMessage, setLocalErrorMessage] = useState(errorMessage);
  // email validation を送信時にしか行わないのであれば、email の変化に合わせる必要はないので、メールアドレスを引数で取る
  const validateEmail = useCallback<EmailValidator>((email: string) => {
    if (validateEmailAddress(email)) {
      return intl.formatMessage({
        id: localeIds.emailValidation.invalid.format,
      });
    } else {
      return undefined;
    }
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();

      // 送信時にだけバリデーションを走らせる
      const validationErrorMessage = validateEmail(email);

      if (validationErrorMessage) {
        setLocalErrorMessage(validationErrorMessage);
      } else {
        onPost(email);
      }
    },
    [email]
  );

  return (
    <form onSubmit={handleSubmit}>
      <InputArea defaultEmail={email} setEmail={setEmail} />

      <Spacer />

      <TextWrapper>
        <FormattedMessage id={localeIds.signUpEmailForm.help}>
          {(text: string): React.ReactElement => (
            <Typography el="p" block bold>
              {text}
            </Typography>
          )}
        </FormattedMessage>
      </TextWrapper>

      {localErrorMessage ? (
        <MessageWrapper>
          <ErrorMessage message={localErrorMessage} maxWidth={MAX_WIDTH} />
        </MessageWrapper>
      ) : null}

      <ButtonWrapper>
        <Button type="submit" style="primary">
          <FormattedMessage id={localeIds.signUpEmailForm.submit} />
        </Button>
      </ButtonWrapper>
    </form>
  );
};
